import React, { useState } from "react";
import { apiFetch } from "@/lib/api";
import { toast } from "@/components/ui/feedback";
import {
  ActionPromptTagGroup,
  ActionPromptTag,
} from "./actionPromptTypes";

interface ActionPromptTagManagerModalProps {
  groups: ActionPromptTagGroup[];
  tags: ActionPromptTag[];
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}

interface GroupFormData {
  id?: string;
  code: string;
  name: string;
  selectionMode: "single" | "multiple";
  matchEnabled: boolean;
  sortOrder: number;
  enabled: boolean;
  originalMode?: "single" | "multiple";
}

export function ActionPromptTagManagerModal({
  groups,
  tags,
  open,
  onClose,
  onChanged,
}: ActionPromptTagManagerModalProps) {
  const [groupForm, setGroupForm] = useState<GroupFormData | null>(null);
  const [groupSubmitting, setGroupSubmitting] = useState(false);
  const [groupConflict, setGroupConflict] = useState<string | null>(null);

  // Inline tag state
  const [inlineTagAddGroupId, setInlineTagAddGroupId] = useState<string | null>(null);
  const [inlineTagAddName, setInlineTagAddName] = useState("");
  const [inlineTagAddCode, setInlineTagAddCode] = useState("");
  const [inlineTagAddSortOrder, setInlineTagAddSortOrder] = useState<number>(0);
  const [inlineTagEditId, setInlineTagEditId] = useState<string | null>(null);
  const [inlineTagEditName, setInlineTagEditName] = useState("");
  const [inlineTagSubmitting, setInlineTagSubmitting] = useState(false);

  if (!open) return null;

  const isSystemGroup = (code: string) => code === "gender" || code === "age_group";

  const tagsForGroup = (groupId: string) => {
    return tags.filter((t) => t.groupId === groupId);
  };

  const startCreateGroup = () => {
    setGroupForm({
      code: "",
      name: "",
      selectionMode: "multiple",
      matchEnabled: false, // Custom groups forced to false!
      sortOrder: (groups.length + 1) * 10,
      enabled: true,
    });
    setGroupConflict(null);
  };

  const startEditGroup = (group: ActionPromptTagGroup) => {
    setGroupForm({
      id: group.id,
      code: group.code,
      name: group.name,
      selectionMode: group.selectionMode,
      matchEnabled: isSystemGroup(group.code) ? true : false,
      sortOrder: group.sortOrder,
      enabled: group.enabled,
      originalMode: group.selectionMode,
    });
    setGroupConflict(null);
  };

  const handleSaveGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupForm) return;

    const trimmedName = groupForm.name.trim();
    if (!trimmedName) {
      toast("请填写标签组名称", "error");
      return;
    }

    const isEditing = Boolean(groupForm.id);
    const trimmedCode = groupForm.code.trim().toLowerCase();

    if (!isEditing) {
      if (!trimmedCode) {
        toast("请填写标签组编码", "error");
        return;
      }
      if (!/^[a-z0-9_]+$/.test(trimmedCode)) {
        toast("编码仅支持小写英文字母、数字和下划线", "error");
        return;
      }
    }

    setGroupSubmitting(true);
    setGroupConflict(null);
    try {
      if (isEditing && groupForm.id) {
        const payload: {
          name: string;
          selectionMode: "single" | "multiple";
          matchEnabled: boolean;
          sortOrder: number;
          enabled: boolean;
        } = {
          name: trimmedName,
          selectionMode: isSystemGroup(groupForm.code) ? "multiple" : groupForm.selectionMode,
          matchEnabled: isSystemGroup(groupForm.code) ? true : false,
          sortOrder: groupForm.sortOrder,
          enabled: groupForm.enabled,
        };

        const res = await apiFetch(`/faceswap/action-prompt-tag-groups/${groupForm.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (res.status === 409) {
          setGroupConflict(
            "模式收敛冲突：已有动作提示词关联了该组下的多个标签，无法直接变更为单选模式。请先调整相关动作提示词的标签后重试。"
          );
          toast("保存失败：存在多标签关联冲突 (409)", "error");
          return;
        }

        if (!res.ok) {
          throw new Error(await res.text());
        }

        toast("标签组已更新", "success");
      } else {
        const payload = {
          code: trimmedCode,
          name: trimmedName,
          selectionMode: groupForm.selectionMode,
          matchEnabled: false,
          sortOrder: groupForm.sortOrder,
          enabled: groupForm.enabled,
        };

        const res = await apiFetch("/faceswap/action-prompt-tag-groups", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (res.status === 409) {
          toast("创建失败：编码或名称已存在 (409)", "error");
          return;
        }

        if (!res.ok) {
          throw new Error(await res.text());
        }

        toast("标签组已创建", "success");
      }

      setGroupForm(null);
      setGroupConflict(null);
      onChanged();
    } catch (err) {
      toast(
        "操作失败：" + (err instanceof Error ? err.message : String(err)),
        "error"
      );
    } finally {
      setGroupSubmitting(false);
    }
  };

  const toggleGroupEnabled = async (group: ActionPromptTagGroup) => {
    if (isSystemGroup(group.code)) {
      toast("系统预设人像匹配标签组不可禁用", "error");
      return;
    }
    try {
      const res = await apiFetch(`/faceswap/action-prompt-tag-groups/${group.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !group.enabled }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast(!group.enabled ? "标签组已启用" : "标签组已禁用", "success");
      onChanged();
    } catch (err) {
      toast("更新失败：" + (err instanceof Error ? err.message : String(err)), "error");
    }
  };

  const handleDeleteGroup = async (group: ActionPromptTagGroup) => {
    if (isSystemGroup(group.code)) {
      toast("系统内置预设匹配标签组不允许删除", "error");
      return;
    }
    if (!window.confirm(`确认删除标签组「${group.name}」？已有标签或引用时会被拒绝。`)) {
      return;
    }

    try {
      const res = await apiFetch(`/faceswap/action-prompt-tag-groups/${group.id}`, {
        method: "DELETE",
      });
      if (res.status === 409) {
        toast("删除失败：该标签组下仍有标签或约束引用，无法直接删除", "error");
        return;
      }
      if (!res.ok) throw new Error(await res.text());
      toast("标签组已删除", "success");
      onChanged();
    } catch (err) {
      toast("删除失败：" + (err instanceof Error ? err.message : String(err)), "error");
    }
  };

  const handleInlineTagAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inlineTagAddGroupId) return;

    const name = inlineTagAddName.trim();
    const code = inlineTagAddCode.trim().toLowerCase();

    if (!name) {
      toast("请填写标签名称", "error");
      return;
    }
    if (!code) {
      toast("请填写标签编码", "error");
      return;
    }
    if (!/^[a-z0-9_]+$/.test(code)) {
      toast("编码仅支持小写英文字母、数字和下划线", "error");
      return;
    }

    setInlineTagSubmitting(true);
    try {
      const res = await apiFetch("/faceswap/action-prompt-tags", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          groupId: inlineTagAddGroupId,
          name,
          code,
          sortOrder: Number(inlineTagAddSortOrder) || 0,
          enabled: true,
        }),
      });

      if (res.status === 409) {
        toast("创建失败：同组下标签编码或名称已存在 (409)", "error");
        return;
      }
      if (!res.ok) throw new Error(await res.text());

      toast("标签已创建", "success");
      setInlineTagAddGroupId(null);
      setInlineTagAddName("");
      setInlineTagAddCode("");
      setInlineTagAddSortOrder(0);
      onChanged();
    } catch (err) {
      toast("创建标签失败：" + (err instanceof Error ? err.message : String(err)), "error");
    } finally {
      setInlineTagSubmitting(false);
    }
  };

  const handleInlineTagEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inlineTagEditId) return;

    const name = inlineTagEditName.trim();
    if (!name) {
      toast("标签名称不能为空", "error");
      return;
    }

    setInlineTagSubmitting(true);
    try {
      const res = await apiFetch(`/faceswap/action-prompt-tags/${inlineTagEditId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error(await res.text());

      toast("标签已更新", "success");
      setInlineTagEditId(null);
      setInlineTagEditName("");
      onChanged();
    } catch (err) {
      toast("更新标签失败：" + (err instanceof Error ? err.message : String(err)), "error");
    } finally {
      setInlineTagSubmitting(false);
    }
  };

  const toggleTagEnabled = async (tag: ActionPromptTag) => {
    try {
      const res = await apiFetch(`/faceswap/action-prompt-tags/${tag.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !tag.enabled }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast(!tag.enabled ? "标签已启用" : "标签已禁用", "success");
      onChanged();
    } catch (err) {
      toast("更新失败：" + (err instanceof Error ? err.message : String(err)), "error");
    }
  };

  const handleDeleteTag = async (tag: ActionPromptTag) => {
    if (!window.confirm(`确认删除标签「${tag.name}」？已有动作引用时服务会拒绝删除。`)) {
      return;
    }

    try {
      const res = await apiFetch(`/faceswap/action-prompt-tags/${tag.id}`, {
        method: "DELETE",
      });
      if (res.status === 409) {
        toast("删除失败：已有动作提示词关联了该标签，无法直接删除", "error");
        return;
      }
      if (!res.ok) throw new Error(await res.text());
      toast("标签已删除", "success");
      onChanged();
    } catch (err) {
      toast("删除失败：" + (err instanceof Error ? err.message : String(err)), "error");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden animate-[popIn_0.25s_ease-out]">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              动作提示词标签管理
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              预设「性别」与「年龄段」参与万相运行时匹配，自定义标签组作为元数据筛选维度。
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto space-y-4 flex-1">
          {/* Top action bar */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              共 {groups.length} 个标签组，{tags.length} 个标签
            </span>
            <button
              type="button"
              onClick={startCreateGroup}
              className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors flex items-center gap-1"
            >
              <i className="ri-add-line text-sm" /> 新建标签组
            </button>
          </div>

          {/* Group Form (Create / Edit) */}
          {groupForm && (
            <form
              onSubmit={handleSaveGroup}
              className="p-4 bg-teal-50/50 border border-teal-200 rounded-xl space-y-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-teal-900">
                  {groupForm.id ? `编辑标签组（${groupForm.name}）` : "新建自定义标签组"}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setGroupForm(null);
                    setGroupConflict(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-xs"
                >
                  取消
                </button>
              </div>

              {groupConflict && (
                <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700 flex items-start gap-2">
                  <i className="ri-error-warning-line shrink-0 text-sm mt-0.5" />
                  <span>{groupConflict}</span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="block text-gray-700 font-medium mb-1">
                    标签组名称 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={groupForm.name}
                    onChange={(e) =>
                      setGroupForm({ ...groupForm, name: e.target.value })
                    }
                    placeholder="例如：场景风格 / 拍摄机位"
                    className="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-1">
                    标签组编码 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    disabled={Boolean(groupForm.id)}
                    value={groupForm.code}
                    onChange={(e) =>
                      setGroupForm({ ...groupForm, code: e.target.value })
                    }
                    placeholder="唯一编码，如 scene_style"
                    className="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-1">
                    选择模式
                  </label>
                  <select
                    disabled={isSystemGroup(groupForm.code)}
                    value={groupForm.selectionMode}
                    onChange={(e) =>
                      setGroupForm({
                        ...groupForm,
                        selectionMode: e.target.value as "single" | "multiple",
                      })
                    }
                    className="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    <option value="multiple">多选（单动作可关联多个）</option>
                    <option value="single">单选（单动作仅可选一个）</option>
                  </select>
                  {isSystemGroup(groupForm.code) && (
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      系统预设匹配组强制为多选模式
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-1">
                    排序权重
                  </label>
                  <input
                    type="number"
                    value={groupForm.sortOrder}
                    onChange={(e) =>
                      setGroupForm({
                        ...groupForm,
                        sortOrder: parseInt(e.target.value, 10) || 0,
                      })
                    }
                    className="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500"
                  />
                </div>
              </div>

              {/* Match enabled note (locked) */}
              <div className="p-2 bg-white/80 rounded border border-gray-200 text-xs text-gray-600">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">运行时画像匹配：</span>
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      isSystemGroup(groupForm.code)
                        ? "bg-rose-50 text-rose-700 border border-rose-200"
                        : "bg-gray-100 text-gray-600 border border-gray-200"
                    }`}
                  >
                    {isSystemGroup(groupForm.code) ? "开启（系统匹配）" : "关闭（仅作元数据）"}
                  </span>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">
                  {isSystemGroup(groupForm.code)
                    ? "性别与年龄段为万相运行时自拍画像分析的唯一输入维度。"
                    : "自定义标签组强制为非匹配元数据，仅用于动作库管理与过滤，不参与运行时人像判定。"}
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setGroupForm(null);
                    setGroupConflict(null);
                  }}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={groupSubmitting}
                  className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 disabled:opacity-60"
                >
                  {groupSubmitting ? "保存中..." : "保存标签组"}
                </button>
              </div>
            </form>
          )}

          {/* Groups List */}
          <div className="space-y-4">
            {groups.map((group) => {
              const isSys = isSystemGroup(group.code);
              const groupTags = tagsForGroup(group.id);
              const isAddingTag = inlineTagAddGroupId === group.id;

              return (
                <div
                  key={group.id}
                  className={`rounded-xl border transition-all ${
                    group.enabled ? "bg-white border-gray-200" : "bg-gray-50/60 border-gray-200 opacity-80"
                  }`}
                >
                  {/* Group Card Header */}
                  <div className="p-3.5 border-b border-gray-100 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-gray-900">
                        {group.name}
                      </span>
                      <span className="text-xs text-gray-400 font-mono">
                        ({group.code})
                      </span>
                      {isSys ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-50 text-rose-700 border border-rose-200">
                          预设匹配组
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700">
                          自定义元数据
                        </span>
                      )}
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700">
                        {group.selectionMode === "single" ? "单选" : "多选"}
                      </span>
                      {!group.enabled && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-700 font-medium">
                          已禁用
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {isSys ? (
                        <span
                          className="px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
                          title="系统预设人像匹配标签组，由系统强制启用"
                        >
                          系统启用
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => toggleGroupEnabled(group)}
                          className={`text-xs px-2 py-1 rounded transition-colors ${
                            group.enabled
                              ? "text-gray-500 hover:bg-gray-100"
                              : "text-teal-600 hover:bg-teal-50"
                          }`}
                          title={group.enabled ? "禁用标签组" : "启用标签组"}
                        >
                          {group.enabled ? "禁用" : "启用"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => startEditGroup(group)}
                        className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="编辑标签组"
                      >
                        <i className="ri-edit-line text-sm" />
                      </button>
                      {!isSys && (
                        <button
                          type="button"
                          onClick={() => handleDeleteGroup(group)}
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="删除标签组"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Group Tags List & Inline Add */}
                  <div className="p-3.5 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {groupTags.map((tag) => {
                        const isEditingThis = inlineTagEditId === tag.id;
                        return isEditingThis ? (
                          <form
                            key={tag.id}
                            onSubmit={handleInlineTagEdit}
                            className="inline-flex items-center gap-1 bg-white border border-teal-300 rounded-full px-2 py-0.5 shadow-sm text-xs"
                          >
                            <input
                              type="text"
                              required
                              value={inlineTagEditName}
                              onChange={(e) => setInlineTagEditName(e.target.value)}
                              className="w-24 px-1 text-xs focus:outline-none"
                              autoFocus
                            />
                            <button
                              type="submit"
                              disabled={inlineTagSubmitting}
                              className="text-teal-600 hover:text-teal-800"
                              title="保存修改"
                            >
                              <i className="ri-check-line" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setInlineTagEditId(null)}
                              className="text-gray-400 hover:text-gray-600"
                              title="取消"
                            >
                              <i className="ri-close-line" />
                            </button>
                          </form>
                        ) : (
                          <span
                            key={tag.id}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                              tag.enabled
                                ? "bg-gray-50 border-gray-200 text-gray-800"
                                : "bg-red-50/50 border-red-200 text-red-600 line-through opacity-70"
                            }`}
                          >
                            <span>{tag.name}</span>
                            <span className="text-[10px] text-gray-400 font-mono">
                              ({tag.code})
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setInlineTagEditId(tag.id);
                                setInlineTagEditName(tag.name);
                              }}
                              className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-amber-600 rounded"
                              title="修改标签名称"
                            >
                              <i className="ri-pencil-line text-[11px]" />
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleTagEnabled(tag)}
                              className={`w-4 h-4 flex items-center justify-center rounded ${
                                tag.enabled
                                  ? "text-gray-400 hover:text-amber-600"
                                  : "text-emerald-600 hover:text-emerald-800"
                              }`}
                              title={tag.enabled ? "停用标签" : "重新启用"}
                            >
                              <i
                                className={
                                  tag.enabled
                                    ? "ri-forbid-line text-[11px]"
                                    : "ri-check-line text-[11px]"
                                }
                              />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteTag(tag)}
                              className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-red-600 rounded"
                              title="删除标签"
                            >
                              <i className="ri-close-line text-[11px]" />
                            </button>
                          </span>
                        );
                      })}

                      {groupTags.length === 0 && !isAddingTag && (
                        <span className="text-xs text-gray-400 italic">
                          暂无标签，点击右侧按钮添加
                        </span>
                      )}

                      {!isAddingTag && (
                        <button
                          type="button"
                          onClick={() => {
                            setInlineTagAddGroupId(group.id);
                            setInlineTagAddName("");
                            setInlineTagAddCode("");
                            setInlineTagAddSortOrder((groupTags.length + 1) * 10);
                          }}
                          className="inline-flex items-center gap-0.5 px-2 py-1 rounded-full text-xs text-teal-600 border border-dashed border-teal-300 hover:bg-teal-50 transition-colors"
                        >
                          <i className="ri-add-line text-xs" />
                          <span>添加标签</span>
                        </button>
                      )}
                    </div>

                    {/* Inline Tag Add Form */}
                    {isAddingTag && (
                      <form
                        onSubmit={handleInlineTagAdd}
                        className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex flex-wrap items-center gap-2 text-xs"
                      >
                        <div className="flex items-center gap-1">
                          <label className="text-gray-600">名称:</label>
                          <input
                            type="text"
                            required
                            value={inlineTagAddName}
                            onChange={(e) => setInlineTagAddName(e.target.value)}
                            placeholder="如: 奔跑 / 欢笑"
                            className="px-2 py-1 bg-white border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 w-28"
                            autoFocus
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <label className="text-gray-600">编码:</label>
                          <input
                            type="text"
                            required
                            value={inlineTagAddCode}
                            onChange={(e) => setInlineTagAddCode(e.target.value)}
                            placeholder="如: running"
                            className="px-2 py-1 bg-white border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 w-24"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <label className="text-gray-600">排序:</label>
                          <input
                            type="number"
                            value={inlineTagAddSortOrder}
                            onChange={(e) =>
                              setInlineTagAddSortOrder(parseInt(e.target.value, 10) || 0)
                            }
                            className="px-2 py-1 bg-white border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 w-16"
                          />
                        </div>
                        <div className="flex items-center gap-1 ml-auto">
                          <button
                            type="button"
                            onClick={() => setInlineTagAddGroupId(null)}
                            className="px-2.5 py-1 border border-gray-200 rounded text-gray-600 hover:bg-white"
                          >
                            取消
                          </button>
                          <button
                            type="submit"
                            disabled={inlineTagSubmitting}
                            className="px-2.5 py-1 bg-teal-600 text-white rounded font-medium hover:bg-teal-700 disabled:opacity-60"
                          >
                            {inlineTagSubmitting ? "创建中..." : "保存标签"}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
